// Peças comuns da organização: cards de uma coleção, miniatura, anel de cor, diálogos (nova coleção, cor, confirmar).
import "../../styles/organize.css";
import { type ComponentChildren, createPortal } from "preact";
import { useEffect, useId, useRef, useState } from "preact/hooks";
import type { Card, Space } from "../../../../shared/types.ts";
import { IconClose } from "../Icons.tsx";
import { cardImage, cardText, safeColor } from "../lib.ts";
import { byId, failed, go, indexTick, showModal, smartSpace, store } from "../state.ts";
import { RingsMark } from "./icons.tsx";
import { colorName, nameTaken, nextColor, SPACE_COLORS } from "./order.ts";

export const ringOf = (color: string | null | undefined) => `--ring:${safeColor(color) ?? "var(--accent)"}`;
export const Ring = ({ color, big }: { color: string; big?: boolean }) => <span class={`ring${big ? " big" : ""}`} style={ringOf(color)} aria-hidden="true" />;
export const spacePath = (id: string) => `/colecoes/${encodeURIComponent(id)}`;
const PRIVATE_Q = /(^|\s)#"?privado\b/i;

/**
 * Cards da coleção, na ordem dela. Manual: pertenças por posição. Automática: o mesmo motor da busca (smartSpace), menos
 * as exclusões, mais as inclusões; reavalia a cada mudança no índice. null enquanto a automática avalia pela primeira vez.
 * Privado fica de fora (só a automática com #privado mostra), como na grade.
 */
export function useSpaceCards(s: Space | undefined): Card[] | null {
  const smart = s?.kind === "smart";
  const [matched, setMatched] = useState<{ id: string; ids: string[] } | null>(null);
  const tick = indexTick.value;
  useEffect(() => {
    if (!s || !smart) return;
    let live = true;
    smartSpace(s.id).then(
      (ids) => live && setMatched({ id: s.id, ids }),
      () => live && setMatched({ id: s.id, ids: [] }),
    );
    return () => void (live = false);
  }, [s?.id, smart, s?.query, tick]);
  if (!s) return null;
  if (smart && matched?.id !== s.id) return null;
  const map = byId.value;
  const priv = smart && PRIVATE_Q.test(s.query ?? "");
  return store
    .spaceCardIds(s.id, smart ? matched!.ids : undefined)
    .map((id) => map.get(id))
    .filter((c): c is Card => !!c && (priv || !c.isPrivate));
}

/** Miniatura de card para pilhas e para a faixa Em foco: a imagem (ou capa) cobrindo; sem imagem, o texto. */
export function Thumb({ card: c }: { card: Card }) {
  const src = cardImage(c, store.previews.value, 320);
  const ph = safeColor(c.dominantColor);
  if (src) {
    return (
      <span class="thumb media" style={ph ? `--ph:${ph}` : undefined}>
        <img src={src} alt="" loading="lazy" decoding="async" onError={(e) => (e.currentTarget.style.visibility = "hidden")} />
      </span>
    );
  }
  return (
    <span class={`thumb text is-${c.kind}`}>
      <span>{cardText(c)}</span>
    </span>
  );
}

/** Diálogo modal pelo portal (fora do topo, que no celular não recebe clique). Fecha com Esc, × ou o botão de cancelar. */
function OrgDialog({ label, class: cls, onClose, children }: { label: string; class?: string; onClose: () => void; children: ComponentChildren }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current!;
    showModal(d);
    return () => d.open && d.close();
  }, []);
  return createPortal(
    <dialog ref={ref} class={`org-dialog ${cls ?? ""}`} aria-labelledby={label} onClose={onClose} onClick={(e) => e.target === e.currentTarget && e.currentTarget.close()}>
      <div class="org-dialog-box">
        {children}
        <button type="button" class="org-dialog-x" aria-label="Fechar" onClick={() => ref.current?.close()}>
          <IconClose />
        </button>
      </div>
    </dialog>,
    document.body,
  );
}
const closeFrom = (el: Element) => el.closest("dialog")?.close();

/** Confirmação de ação sem volta (apagar coleção, apagar ou mesclar etiquetas). O foco abre no "Cancelar". */
export function Confirm({ title, line, action, danger, children, onConfirm, onClose }: { title: string; line: string; action: string; danger?: boolean; children?: ComponentChildren; onConfirm: () => Promise<unknown> | void; onClose: () => void }) {
  const id = useId();
  const [busy, setBusy] = useState(false);
  return (
    <OrgDialog label={`${id}-t`} class="confirm" onClose={onClose}>
      <h2 id={`${id}-t`} class="org-dialog-title">
        {title}
      </h2>
      <p class="org-dialog-line">{line}</p>
      {children}
      <div class="org-dialog-actions">
        <button
          type="button"
          class={`org-btn${danger ? " danger" : ""}`}
          disabled={busy}
          onClick={async (e) => {
            const el = e.currentTarget;
            setBusy(true);
            try {
              await onConfirm();
            } catch {
              failed();
            }
            closeFrom(el);
          }}
        >
          {action}
        </button>
        <button type="button" class="org-cancel" autofocus onClick={(e) => closeFrom(e.currentTarget)}>
          Cancelar
        </button>
      </div>
    </OrgDialog>
  );
}

/** Anel de 16 cores em volta da prévia (rádios nativos: as setas trocam a cor). */
export function ColorRing({ value, label, onChange }: { value: string; label: string; onChange: (hex: string) => void }) {
  const name = useId();
  return (
    <fieldset class="color-ring">
      <legend class="sr-only">Cor da coleção</legend>
      <span class="color-ring-center" aria-hidden="true">
        <Ring color={value} big />
        <span class="color-ring-name">{label}</span>
      </span>
      {SPACE_COLORS.map(([hex, title], i) => (
        <label class="swatch" key={hex} style={`--sw:${hex};--i:${i}`} title={title}>
          <input type="radio" name={name} value={hex} checked={value.toLowerCase() === hex.toLowerCase()} onChange={() => onChange(hex)} />
          <span class="sr-only">{title}</span>
        </label>
      ))}
    </fieldset>
  );
}

export type SpaceDialogMode = { kind: "create"; query?: string } | { kind: "color"; space: Space };

/**
 * Nova coleção (nome → cor), manual ou da busca atual (automática), ou trocar a cor de uma existente.
 * "Próximo" fica desativado até haver nome; nome repetido é recusado ali mesmo. Criada, abre a coleção.
 */
export function SpaceDialog({ mode, onClose }: { mode: SpaceDialogMode; onClose: () => void }) {
  const id = useId();
  const editing = mode.kind === "color" ? mode.space : null;
  const query = mode.kind === "create" ? mode.query : undefined;
  const [step, setStep] = useState(editing ? 2 : 1);
  const [name, setName] = useState("");
  const [color, setColor] = useState(editing?.color ?? nextColor(store.spaces.value.map((s) => s.color)));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const form = useRef<HTMLFormElement>(null);
  // Trocar de passo tira o botão focado da tela: o foco vai para o campo do passo novo (nome ou a cor marcada).
  const first = useRef(true);
  useEffect(() => {
    if (first.current) return void (first.current = false);
    form.current?.querySelector<HTMLElement>("input:checked, input")?.focus();
  }, [step]);
  const title = editing ? "Cor da coleção" : query ? "Coleção automática" : "Nova coleção";
  const line = editing
    ? `Escolha a cor de “${editing.name}”.`
    : query
      ? "Guarda esta busca como coleção. Ela se atualiza sozinha: cards novos que combinarem entram nela."
      : step === 1
        ? "Junte cards por assunto, do seu jeito."
        : "Agora, uma cor para achar de longe.";

  const next = (e: Event) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    if (nameTaken(store.spaces.value, n)) return setError("Já existe uma coleção com esse nome.");
    setStep(2);
  };
  const save = async (e: Event) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    if (busy) return;
    setBusy(true);
    try {
      if (editing) {
        await store.updateSpace(editing.id, { color });
        return closeFrom(form);
      }
      const created = await store.createSpace({ name: name.trim(), color, ...(query ? { kind: "smart" as const, query } : {}) });
      if (!created) {
        setError("Já existe uma coleção com esse nome.");
        return setStep(1);
      }
      closeFrom(form);
      go(spacePath(created));
    } catch {
      failed();
    } finally {
      setBusy(false);
    }
  };

  return (
    <OrgDialog label={`${id}-t`} class="space-dialog" onClose={onClose}>
      <span class="org-dialog-mark">
        <RingsMark />
      </span>
      <h2 id={`${id}-t`} class="org-dialog-title">
        {title}
      </h2>
      <p class="org-dialog-line">{line}</p>
      {query && (
        <p class="sd-query">
          <span class="sr-only">Busca: </span>
          {query.split(/\s+/).map((t, i) => (
            <span class="chip" key={i}>
              {t}
            </span>
          ))}
        </p>
      )}
      {step === 1 ? (
        <form class="sd-form" ref={form} onSubmit={next}>
          <input
            class="sd-name"
            aria-label="Nome da coleção"
            aria-invalid={error ? "true" : undefined}
            aria-describedby={error ? `${id}-e` : undefined}
            placeholder="Dê um nome à coleção"
            autocomplete="off"
            maxLength={80}
            autofocus
            value={name}
            onInput={(e) => (setName(e.currentTarget.value), setError(""))}
          />
          {error && (
            <p id={`${id}-e`} class="sd-error" role="alert">
              {error}
            </p>
          )}
          <button class="org-btn" disabled={!name.trim()}>
            Próximo
          </button>
        </form>
      ) : (
        <form class="sd-form" ref={form} onSubmit={save}>
          <ColorRing value={color} label={editing ? editing.name : name.trim() || colorName(color)} onChange={setColor} />
          <button class="org-btn" disabled={busy}>
            {editing ? "Salvar cor" : query ? "Salvar coleção" : "Criar coleção"}
          </button>
          {!editing && (
            <button type="button" class="org-cancel" onClick={() => setStep(1)}>
              Voltar
            </button>
          )}
        </form>
      )}
    </OrgDialog>
  );
}
